'use es6';

import { PopoverType } from 'rich-text-lib/constants/popover';
import { trackInteraction } from '../utils/usageTracking';
import { INSERT_TEMP_ANCHOR, INSERT_ANCHOR, EDIT_ANCHOR, DRAG_DROP_ANCHOR } from '../constants/usageTracking';
import { ANCHOR_CLASSNAME, TEMP_ANCHOR_ATTRIBUTE, HS_ANCHOR_ATTRIBUTE, AnchorBlockSelectors } from '../constants/hsanchor';
import { isAnchorNode, isListTable } from '../utils/anchor';
let dragTarget;

// Find the eligible parent block that the anchor node will be inserted before
const getParentAnchorBlock = editor => {
  const body = editor.getBody();
  let parentBlock = editor.selection.getNode();
  const listTable = isListTable(parentBlock);
  if (listTable) return listTable;
  if (body === parentBlock) {
    // If the cursor lives as a direct child of the editor body, then we want to
    // insert the anchor right after the selected node
    const curSel = editor.selection.getSel().anchorNode;
    if (curSel !== body) {
      return curSel.nextSibling || curSel;
    }

    // When the cursor has no elements, we need to make a bookmark to tether to
    const bookmark = editor.selection.getBookmark();
    const bookmarkNode = editor.dom.get(`${bookmark.id}_start`);
    return bookmarkNode;
  }
  Object.keys(AnchorBlockSelectors).forEach(tier => {
    parentBlock = editor.dom.getParent(parentBlock, AnchorBlockSelectors[tier], body) || parentBlock;
  });
  return parentBlock;
};
export const getInsertAnchorHandler = editor => (target, id) => {
  editor.dom.setAttribs(target, {
    id,
    [TEMP_ANCHOR_ATTRIBUTE]: null,
    [HS_ANCHOR_ATTRIBUTE]: true
  });
  editor.undoManager.add();
  editor.selection.select(target);
  trackInteraction(editor, 'tinymceAnchor', INSERT_ANCHOR, {
    insertLocation: target.nextSibling.nodeName
  });
};
export const getUpdateAnchorHandler = editor => (node, id) => {
  editor.undoManager.transact(() => {
    editor.dom.setAttrib(node, 'id', id);
  });
  trackInteraction(editor, 'tinymceAnchor', EDIT_ANCHOR);
};
export const getAnchorRemoveHandler = editor => node => {
  editor.undoManager.transact(() => {
    editor.dom.remove(node);
  });
};
export const getRemoveTempAnchorsHandler = editor => () => {
  if (dragTarget) return; // Don't remove the temp anchor if it's being dragged
  editor.dom.select(`a[${TEMP_ANCHOR_ATTRIBUTE}]`).forEach(node => {
    editor.dom.remove(node);
  });
};
export const getInsertTempAnchorHandler = editor => () => {
  const blockNode = getParentAnchorBlock(editor);
  const tempAnchor = editor.dom.create('a', {
    class: ANCHOR_CLASSNAME,
    contenteditable: 'false',
    [TEMP_ANCHOR_ATTRIBUTE]: 'true',
    'data-mce-selected': 'true'
  });
  const insertAnchor = () => {
    if (blockNode) {
      blockNode.parentNode.insertBefore(tempAnchor, blockNode);
    } else {
      const selectedNode = editor.selection.getNode();
      selectedNode.parentNode.insertBefore(tempAnchor, selectedNode);
    }
  };
  if (editor.undoManager.ignore) {
    editor.undoManager.ignore(() => {
      insertAnchor();
    });
  } else {
    // Because the Inpage Editor does not have [ignore] as an undo/redo event
    insertAnchor();
  }
  trackInteraction(editor, 'tinymceAnchor', INSERT_TEMP_ANCHOR, {
    insertLocation: editor.selection.getNode().nodeName
  });
  return tempAnchor;
};
export const getAnchorDragStartHandler = () => ({
  target
}) => {
  if (isAnchorNode(target)) {
    dragTarget = target;
  }
};
export const getAnchorDropHandler = editor => evt => {
  const dropNode = getParentAnchorBlock(editor);
  if (dropNode && dragTarget) {
    // Override tinymce drop handler in order to insert anchor outside and
    // before block elements as a direct child of the editor body
    editor.undoManager.transact(() => {
      dropNode.parentNode.insertBefore(dragTarget, dropNode);
    });

    // re-render the popover
    const popoverType = dragTarget.getAttribute(TEMP_ANCHOR_ATTRIBUTE) ? PopoverType.INSERT : PopoverType.EDIT;
    editor.selection.select(dragTarget);
    trackInteraction(editor, 'tinymceAnchor', DRAG_DROP_ANCHOR, {
      insertLocation: dropNode.nodeName,
      dragStage: popoverType
    });
    dragTarget = null;
    evt.preventDefault();
    evt.stopPropagation();
  }
};
export const getAnchorClickHandler = editor => () => {
  const isActive = isAnchorNode(editor.selection.getNode());
  if (isActive) {
    editor.fire('ToggleAnchorEditPopover');
  } else {
    editor.fire('OpenAnchorPopover');
  }
};

// Deletes up through the anchor node as the default keyboard delete
// functionality gets blocked
const removeAnchorOnBackspace = (editor, evt) => {
  const body = editor.getBody();
  const rng = editor.selection.getRng();
  let cursorNode = rng.startContainer;
  let shouldDeleteAnchor = false;
  if (cursorNode.parentNode === body && cursorNode.nodeName === '#text') {
    shouldDeleteAnchor = isAnchorNode(cursorNode.previousSibling);
  } else {
    if (rng.startOffset !== 0) return;
    let parentBlock = cursorNode.parentNode;
    while (parentBlock !== body) {
      if (parentBlock.firstChild !== cursorNode) {
        shouldDeleteAnchor = false;
        break;
      }
      cursorNode = parentBlock;
      parentBlock = parentBlock.parentNode;
    }
    shouldDeleteAnchor = isAnchorNode(cursorNode.previousSibling);
  }
  if (shouldDeleteAnchor) {
    editor.dom.remove(cursorNode.previousSibling);
    evt.preventDefault();
  }
};

// Quirky behavior with {display: block} and contenteditable="false"
// where the cursor gets stuck on a ZWNBS unicode character (&#65279)
const skipOverAnchor = (editor, evt) => {
  const cursorNode = editor.selection.getRng().startContainer;
  if (cursorNode.parentNode !== editor.getBody() || cursorNode.nodeName !== '#text') return;
  if ((evt.key === 'ArrowDown' || evt.key === 'ArrowRight') && isAnchorNode(cursorNode.nextSibling)) {
    editor.selection.setCursorLocation(cursorNode.nextSibling);
    evt.preventDefault();
  } else if (evt.key === 'ArrowLeft' && isAnchorNode(cursorNode.previousSibling)) {
    const prevNode = cursorNode.previousSibling.previousSibling;
    if (prevNode) {
      editor.selection.select(prevNode);
      editor.selection.collapse();
      evt.preventDefault();
    }
  }
};
export const getAnchorKeydownHandler = editor => evt => {
  if (evt.key === 'Backspace' && editor.selection.isCollapsed()) {
    removeAnchorOnBackspace(editor, evt);
  }
  if (evt.key === 'ArrowDown' || evt.key === 'ArrowRight' || evt.key === 'ArrowLeft') {
    skipOverAnchor(editor, evt);
  }
};

// Toggles the visual aid, i.e. class="mce-hs-anchor" for anchor elements
export const getToggleAnchorVisualAidHandler = (editor, showVisual) => () => {
  const anchors = editor.getBody().querySelectorAll(`a[${HS_ANCHOR_ATTRIBUTE}]`);
  [...anchors].forEach(anchor => {
    if (showVisual) {
      anchor.classList.add(ANCHOR_CLASSNAME);
    } else {
      anchor.classList.remove(ANCHOR_CLASSNAME);
    }
  });
};